import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trash2, Mail, AlertTriangle, CheckCircle } from 'lucide-react';

const AccountDeletion: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    // API call to backend
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/v1/delete-request/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Deletion request response:', data);
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting deletion request:', err);
      setError('An error occurred while submitting your request. Please try again or contact support.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors duration-200 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-textBase">Delete Account</h1>
              <p className="text-grey-600">Request account deletion</p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm p-8"
        >
          {!isSubmitted ? (
            <>
              {/* Warning Section */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-6 h-6 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-800 mb-2">
                      Important: Account Deletion is Permanent
                    </h3>
                    <p className="text-red-700 leading-relaxed">
                      Once your account is deleted, all your data including financial goals, transaction history, 
                      and progress tracking will be permanently removed and cannot be recovered. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              {/* What Will Be Deleted */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-textBase mb-4">What Will Be Deleted</h2>
                <ul className="space-y-3 text-grey-600">
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Your account profile and personal information</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>All financial goals and targets you've set</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Transaction history and spending data</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Progress tracking and analytics</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>App preferences and settings</span>
                  </li>
                </ul>
              </div>

              {/* Deletion Process */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-textBase mb-4">Deletion Process</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <ol className="space-y-4 text-grey-600">
                    <li className="flex items-start space-x-3">
                      <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">1</span>
                      <span>Submit your email address below</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">2</span>
                      <span>We'll send a confirmation email to verify your request</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">3</span>
                      <span>Click the confirmation link in the email to proceed</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">4</span>
                      <span>Your account and data will be permanently deleted within 30 days</span>
                    </li>
                  </ol>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-textBase mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-grey-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 pr-3 py-3 border border-grey-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                      placeholder="Enter your account email address"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  {error && (
                    <p className="mt-2 text-sm text-red-600">{error}</p>
                  )}
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="confirm"
                    required
                    className="mt-1 h-4 w-4 text-primary focus:ring-primary border-grey-300 rounded"
                    disabled={isLoading}
                  />
                  <label htmlFor="confirm" className="text-sm text-grey-600">
                    I understand that this action is permanent and cannot be undone. I confirm that I want to delete my account and all associated data.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5" />
                      <span>Request Account Deletion</span>
                    </>
                  )}
                </button>
              </form>

              {/* Alternative Options */}
              <div className="mt-8 pt-6 border-t border-grey-200">
                <h3 className="text-lg font-semibold text-textBase mb-4">Not Sure? Consider These Alternatives</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-grey-600">Temporarily deactivate your account instead</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-grey-600">Export your data before deletion</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-grey-600">Contact support for assistance</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Success Message */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-semibold text-textBase mb-4">
                Deletion Request Submitted
              </h2>
              <p className="text-grey-600 mb-6">
                We've received your account deletion request. Please check your email at <strong>{email}</strong> 
                for a confirmation link to complete the process.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-800 text-sm">
                  <strong>Note:</strong> If you don't receive the email within 10 minutes, please check your spam folder 
                  or contact our support team.
                </p>
              </div>
              <Link
                to="/"
                className="inline-flex items-center text-primary hover:text-primary-dark transition-colors duration-200"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Home
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AccountDeletion;
